// Authentication via d3-auth (delegates to the vendored d3-kit bundle)

export { authState } from '@dice-driven/d3-kit';

import { Capacitor, registerPlugin } from '@capacitor/core';
import { authState } from '@dice-driven/d3-kit';

const SecureStorage = Capacitor.isNativePlatform() ? registerPlugin('SecureStorage') : null;

// On native Capacitor, use system browser (SFSafariViewController / Chrome
// Custom Tabs) for OAuth login and logout so the d3-auth session is properly
// cleared and redirects fire via the native appUrlOpen mechanism. The
// loginRedirectUri and logoutReturnTo are passed to authState.init() from
// rpgish-app.js so they vary by platform. Tokens are stored in Keychain /
// EncryptedSharedPreferences via SecureStorage instead of localStorage so
// they're encrypted at rest and not accessible to other processes.
const _capacitorAuthReady = Capacitor.isNativePlatform()
  ? import('./capacitor-auth.js')
      .then(({ openInAppBrowserLogin, openInAppBrowserLogout }) => {
        authState.setLoginOpener(openInAppBrowserLogin);
        authState.setLogoutOpener(openInAppBrowserLogout);
        authState.setStorage({
          get: async (key) => (await SecureStorage.get({ key })).value,
          set: (key, value) => SecureStorage.set({ key, value }),
          remove: (key) => SecureStorage.remove({ key }),
        });
      })
      .catch((err) => {
        console.error('Capacitor auth wiring failed:', err);
      })
  : Promise.resolve();

/**
 * Call this instead of authState.init() directly — it waits for the
 * Capacitor-specific setup (login/logout openers and secure storage adapter)
 * to be wired up before initializing auth, otherwise init would read from
 * localStorage instead of Keychain/EncryptedSharedPreferences on native.
 * @param {Parameters<typeof authState.init>[0]} config
 */
export const initAuth = async (config) => {
  await _capacitorAuthReady;
  return authState.init(config);
};

// Convenience exports — login waits for the opener to be set on Capacitor
export const login = async () => {
  await _capacitorAuthReady;
  return authState.login();
};
export const logout = () => authState.logout();

/**
 * Check if user has staff access (operate:rpgish permission from d3-auth)
 * @returns {boolean}
 */
export function isStaff() {
  return authState.user?.permissions?.includes('operate:rpgish') ?? false;
}
